import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ParsedRecord } from "@/features/patient/records/api/dataPipelineApi";
import { invalidatePatientData, recordsQuery } from "@/portals/patient/api/patientQueries";
import { useFamily } from "./FamilyContext";

// Every parsed record of the viewed profile (all resource types, across all
// completed uploads and syncs). Backs the Dashboard's stat cards; Diagnostic
// Reports and Visits are selectors over it. Served from the shared query
// cache: every screen reading it shares one request, a revisit shows the
// cached copy at once and revalidates in the background, and switching
// profile switches cache entry (never shows the previous profile's records).
// Named ParsedRecords (not "records") to keep it apart from the legacy,
// client-only PatientDataContext.records.
const EMPTY: ParsedRecord[] = [];

interface ParsedRecordsValue {
  records: ParsedRecord[];
  /** True only while there is nothing to show yet — never on a background refresh. */
  isLoading: boolean;
  error: string | null;
  /** Refetches records, medications and the review queue for this profile. */
  refresh: () => void;
  /** Changes whenever a fresh copy of the records lands. */
  refreshToken: number;
}

export function useParsedRecords(): ParsedRecordsValue {
  const { activeDependent } = useFamily();
  const profileId = activeDependent?.id;
  const client = useQueryClient();
  const query = useQuery(recordsQuery(profileId));
  const refresh = useCallback(() => void invalidatePatientData(client, profileId), [client, profileId]);
  return {
    records: query.data ?? EMPTY,
    isLoading: query.isPending,
    error: query.isError && query.data === undefined ? "Couldn't load your records. Please try again later." : null,
    refresh,
    refreshToken: query.dataUpdatedAt,
  };
}
